import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AUTH_CHANGED_EVENT } from '../lib/supabase';

const SubscriptionContext = createContext(null);
const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'canceling', 'past_due', 'trialing']);
const CANCELED_SUBSCRIPTION_STATUSES = new Set(['canceled']);

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptionStatus = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    setSubscription({
      subscription_status: 'active',
      cancel_at_period_end: false,
      plan_name: 'FakturaOne Pro',
      amount: 2900,
      currency: 'eur',
      active_count: 1,
      current_period_end: null,
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSubscriptionStatus();

    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleAuthChanged = () => {
      fetchSubscriptionStatus();
    };
    window.addEventListener(AUTH_CHANGED_EVENT, handleAuthChanged);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, handleAuthChanged);
    };
  }, [fetchSubscriptionStatus]);

  const subscriptionStatus = subscription?.subscription_status || 'inactive';
  const subscriptionEnd = subscription?.current_period_end;
  const isActive = ACTIVE_SUBSCRIPTION_STATUSES.has(subscriptionStatus);
  const isCanceling = subscriptionStatus === 'canceling' || subscription?.cancel_at_period_end;
  const isCanceled = CANCELED_SUBSCRIPTION_STATUSES.has(subscriptionStatus);
  const needsSubscription = false;

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    await fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      subscriptionStatus,
      subscriptionEnd,
      loading,
      isActive,
      isCanceling,
      isCanceled,
      needsSubscription,
      refreshStatus,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
